import { httpsCallable } from "firebase/functions";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, WifiOff } from "lucide-react";
import Swal from 'sweetalert2';
import { useAuth, User } from "../AuthContext";
import AvatarSelector from "../AvatarSelector";
import CallSettings, { CallSettingsHandle, MediaPermissionState } from "./CallSettings";
import { AccessRoomResponseStatus, MeetingEntranceRequest, SessionSettingsResponse } from "./types";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { db, auth, functions } from "../../firebase";
import { browserLocalPersistence, setPersistence, signInAnonymously, updateProfile } from "firebase/auth";
import { applyLobbyAccessFallback, getDevLobbyDebugLabel } from "./lobbyAccessFallback";
import { applyDevTestLobbyOverride, canProceedToDevTestLobby, isDevBuild, isDevTestRoomId } from "./devMeetingLobby";
import { readRejoinIntent } from "./roomRejoin";
import { evaluateNetworkQuality, networkWarningMessage, subscribeNetworkChanges } from "./networkQuality";
import { getSessionMeetingCollection, isRoomOwnerById, expectedOwnerUidFromRoomId, type MeetingVideoEngine } from "../../utils/sessionMeeting";
import { isLiveKitOpenTestRoom } from "../../utils/livekitOpenTestRoom";

type LobbyProps = {
    user: User | null;
    roomId: string;
    forceEngine?: MeetingVideoEngine;
    onFinishConfiguration: (validationResponse: SessionSettingsResponse, avatarId: string | null, sessionIdentityKey: string | null) => void;
}

const SESSION_IDENTITY_STORAGE_KEY = "meeting:sessionIdentityKey";

const ensureSessionIdentityKey = () => {
    try {
        const existing = window.localStorage.getItem(SESSION_IDENTITY_STORAGE_KEY);
        if (existing) {
            return existing;
        }

        const generated = `sid_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
        window.localStorage.setItem(SESSION_IDENTITY_STORAGE_KEY, generated);
        return generated;
    } catch (error) {
        // Em ambientes com storage bloqueado, mantemos a funcionalidade sem quebrar o fluxo.
        return null;
    }
};

const Lobby = ({ user, roomId, forceEngine, onFinishConfiguration }: LobbyProps) => {
    const { updateUserAvatar, isLoading, refreshAuthUser, logout } = useAuth();
    const [networkWarning, setNetworkWarning] = useState<string | null>(null);
    const [forceAvatarSelection, setForceAvatarSelection] = useState<boolean>(false);
    const [lobbyMessage, setLobbyMessage] = useState<string>("");
    const [lobbyTitle, setLobbyTitle] = useState<string>("Prepare-se para uma experiência transformadora!");
    const [validationResponse, setValidationResponse] = useState<SessionSettingsResponse | null>(null);
    const [showAvatarSelector, setShowAvatarSelector] = useState<boolean>(false);
    const [isEntranceAllowed, setIsEntranceAllowed] = useState<boolean>(false);
    const [isRequestingEntrance, setIsRequestingEntrance] = useState<boolean>(false);
    const [isExiting, setIsExiting] = useState<boolean>(false);
    const [showAnonymousLoginModal, setShowAnonymousLoginModal] = useState<boolean>(false);
    const [anonymousUserName, setAnonymousUserName] = useState<string>("");
    const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);
    const [mediaPermissionState, setMediaPermissionState] = useState<MediaPermissionState>('idle');
    const hasTransitionedToRoomRef = useRef<boolean>(false);
    const autoRejoinTriedRef = useRef<boolean>(false);
    const validationAttemptedForRoomRef = useRef<string | null>(null);
    const callSettingsRef = useRef<CallSettingsHandle | null>(null);

    useEffect(() => {
        setValidationResponse(null);
        setIsEntranceAllowed(false);
        setIsRequestingEntrance(false);
        setForceAvatarSelection(false);
        setLobbyTitle("Prepare-se para uma experiência transformadora!");
        setLobbyMessage("");
        hasTransitionedToRoomRef.current = false;
        autoRejoinTriedRef.current = false;
        validationAttemptedForRoomRef.current = null;
    }, [roomId]);

    const validateMeetingEntrance = httpsCallable<MeetingEntranceRequest, SessionSettingsResponse>(
        functions,
        "validateMeetingEntrance",
    );

    const requestRoomAccessFunction = httpsCallable<{ roomId: string; sessionIdentityKey?: string | null }, { success: boolean; message: string }>(
        functions,
        "requestRoomAccess",
    );

    const cancelRoomAccessRequestFunction = httpsCallable<{ roomId: string }, { success: boolean; message: string }>(
        functions,
        "cancelRoomAccessRequest",
    );

    const handleAnonymousLogin = useCallback(async () => {
        if (!anonymousUserName.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Nome obrigatório',
                text: 'Por favor, informe seu nome para continuar.',
            });
            return;
        }

        setIsAuthenticating(true);

        try {
            // Persistência LOCAL (e não de sessão) para o convidado anônimo manter
            // o MESMO uid após o sistema operacional matar a aba em segundo plano
            // (cenário típico de queda no celular). Com sessionStorage, cada
            // reabertura gerava um uid novo e a pessoa perdia a aprovação de
            // entrada (tinha que "pedir permissão" de novo), o avatar/posição e o
            // status de co-admin. localStorage sobrevive ao kill do processo, então
            // o uid passa a ser estável como o de um usuário cadastrado.
            await setPersistence(auth, browserLocalPersistence);
            const userCredential = await signInAnonymously(auth);
            const firebaseUser = userCredential.user;

            // Criar documento no Firestore PRIMEIRO
            // Isso garante que quando o onAuthStateChanged disparar,
            // os dados já estarão disponíveis no Firestore
            await setDoc(doc(db, "users", firebaseUser.uid), {
                uid: firebaseUser.uid,
                name: anonymousUserName.trim(),
                email: firebaseUser.email || null,
                role: "participante",
                avatar: "",
                status: "ativo",
                createdAt: new Date(),
                isAnonymous: true,
                needsPasswordSetup: false
            });

            // Atualizar o displayName no Firebase Auth
            await updateProfile(firebaseUser, {
                displayName: anonymousUserName.trim()
            });

            // Forçar atualização do user no contexto para garantir que name seja populado
            // antes de addUserToRoom ser chamado (evita race condition com onAuthStateChanged)
            await refreshAuthUser();

            setShowAnonymousLoginModal(false);
            setAnonymousUserName("");
        } catch (error) {
            console.error("Erro ao autenticar:", error);
            Swal.fire({
                icon: 'error',
                title: 'Erro ao autenticar',
                text: 'Não foi possível fazer login. Por favor, tente novamente.',
            });
        } finally {
            setIsAuthenticating(false);
        }
    }, [anonymousUserName, refreshAuthUser]);

    // Verificar se o usuário está autenticado (apenas na montagem inicial)
    // Só mostra o modal de login anônimo após o carregamento inicial terminar
    useEffect(() => {
        if (!isLoading && !user) {
            setShowAnonymousLoginModal(true);
        }
    }, [user, isLoading]);

    // Preflight de rede: avisa antes de entrar quando 2G/3G ou latência alta.
    useEffect(() => {
        const refresh = () => {
            const snapshot = evaluateNetworkQuality();
            if (!snapshot || snapshot.tier === "good") {
                setNetworkWarning(null);
                return;
            }
            setNetworkWarning(
                networkWarningMessage(snapshot) ??
                    "Sua conexão parece instável. Para evitar quedas, prefira Wi‑Fi estável e mantenha esta tela em primeiro plano durante a sessão.",
            );
        };

        refresh();
        return subscribeNetworkChanges(refresh);
    }, []);

    const reloadAfterDelay = (delayMs: number) => {
        setTimeout(() => {
            window.location.reload();
        }, delayMs);
    };

    const handleMeetingEntrance = useCallback(async () => {
        if (!roomId || !user) {
            setLobbyTitle("Acesso à sala indisponível");
            setLobbyMessage("Dados insuficientes para entrar na sala.");
            return;
        }

        if (validationAttemptedForRoomRef.current === roomId) {
            return;
        }

        setLobbyMessage("Verificando acesso à sala...");
        validationAttemptedForRoomRef.current = roomId;

        const isOwnerForRoom = isRoomOwnerById(roomId, user.uid);
        const isOpenTestRoom = forceEngine === "livekit" && isLiveKitOpenTestRoom(roomId);

        try {
            let { data: response } = await validateMeetingEntrance({
                roomId,
                preferLiveKit: forceEngine === "livekit",
            });
            response = await applyLobbyAccessFallback(response, roomId, user.uid);

            if (isOpenTestRoom) {
                response = {
                    ...response,
                    canAccessRoom: true,
                    entryApproved: true,
                    userHasCredits: true,
                    isRoomOwner: false,
                    livekitOpenTest: true,
                    isTheRoomFull: false,
                };
            }

            // Beta LiveKit: dono da sala (sala-{uid}) sempre entra — independente do backend.
            if (forceEngine === "livekit" && isOwnerForRoom && !isOpenTestRoom) {
                response = {
                    ...response,
                    canAccessRoom: true,
                    entryApproved: true,
                    isRoomModerator: true,
                    isRoomOwner: true,
                    isTherapistUser: true,
                    sessionRole: "terapeuta",
                    userHasCredits: response.userHasCredits !== false ? response.userHasCredits : true,
                };
            } else if (
                forceEngine === "livekit" &&
                !response.canAccessRoom &&
                isOwnerForRoom
            ) {
                response = {
                    ...response,
                    canAccessRoom: true,
                    entryApproved: true,
                    isRoomModerator: true,
                    isRoomOwner: true,
                    sessionRole: "terapeuta",
                };
            }

            if (response.isUserInMeeting) {
                setLobbyTitle("Acesso à sala indisponível");
                setLobbyMessage("⚠️ Identificamos que você já está conectado a esta sala em outra aba ou dispositivo, caso tenha se desconectado, por favor aguarde alguns instantes e atualize a página.");

                reloadAfterDelay(5000);
                return;
            }

            setValidationResponse(response);

            if (response.devTestOpen) {
                setIsEntranceAllowed(true);
            }

            if (isOpenTestRoom) {
                setLobbyTitle("Sala de teste LiveKit");
                setLobbyMessage(
                    "Entrada livre para testes. O primeiro a entrar vira administrador; ao sair, o admin passa para outra pessoa.",
                );
            } else if (response.isRoomModerator) {
                setLobbyTitle("Você é o maestro dessa história!");
            }

            const shouldRequireAvatarSelection = response.isRoomModerator && !user.avatar;
            setForceAvatarSelection(shouldRequireAvatarSelection);

            if (response.isTheRoomFull) {
                setLobbyTitle("Sala cheia");
                setLobbyMessage("❌ A sala atingiu o número máximo de participantes. Por favor, tente novamente mais tarde.");
                reloadAfterDelay(15000);
                return;
            }

            if (!response.userHasCredits && !(isDevBuild() && response.devTestOpen)) {
                setLobbyTitle("Créditos insuficientes");
                setLobbyMessage("❌ Você não possui créditos suficientes para acessar esta sala, faça uma recarga para continuar.");
                return;
            }
            
            if (!response.canAccessRoom) {
                setLobbyTitle("Acesso negado à sala");
                const ownerUid = expectedOwnerUidFromRoomId(roomId);
                const ownerHint =
                    forceEngine === "livekit" && ownerUid && user.uid !== ownerUid
                        ? ` Você está logado como ${user.email || user.uid}, mas o dono desta sala é outro usuário (uid ${ownerUid.slice(0, 8)}…).`
                        : "";
                if (!response.dyteMeetingAlreadyExists) {
                    setLobbyMessage(
                        (forceEngine === "livekit"
                            ? "❌ Você não tem permissão para entrar nesta sala antes da sessão começar. O terapeuta dono deve entrar primeiro em /sala-beta/… — ou peça um convite / link de sala pública."
                            : "❌ Você não tem permissão para entrar nesta sala antes da sessão começar. Peça ao terapeuta um convite, use um link de sala pública ou aguarde a sessão ser iniciada.") +
                            ownerHint,
                    );
                    return;
                }
                if (response.sessionRole === 'participant') {
                    setLobbyMessage(
                        "A sessão já está em andamento. Toque em \"Solicitar entrada\" abaixo e aguarde o terapeuta liberar seu acesso." +
                            ownerHint,
                    );
                } else {
                    setLobbyMessage("❌ Você não tem permissão para acessar esta sala. Verifique com o terapeuta se seu acesso foi configurado corretamente.");
                    return;
                }
            }

            if (!response.dyteMeetingAlreadyExists && !response.isTherapistUser) {
                setLobbyMessage("O terapeuta ainda não iniciou a sessão. Por favor, aguarde.");
                reloadAfterDelay(15000);
            } else if (!response.entryApproved && !response.isRoomModerator) {
                setLobbyMessage(
                    response.dyteMeetingAlreadyExists
                        ? "A sessão já está em andamento. Toque em \"Solicitar entrada\" e aguarde o terapeuta liberar seu acesso."
                        : "Aguardando aprovação explícita do terapeuta para entrar na sala.",
                );
            } else {
                setLobbyMessage("Ajuste suas configurações e clique no botão para entrar.");
            }

            setIsEntranceAllowed(response.entryApproved);
        } catch (error) {
            console.error("Erro ao validar acesso à sala:", error);

            if (forceEngine === "livekit" && isRoomOwnerById(roomId, user.uid)) {
                const ownerFallback: SessionSettingsResponse = {
                    isTherapistUser: true,
                    isRoomModerator: true,
                    isRoomOwner: true,
                    isUserInMeeting: false,
                    canAccessRoom: true,
                    isTheRoomFull: false,
                    userHasCredits: true,
                    entryApproved: true,
                    sessionRole: "terapeuta",
                    dyteMeetingAlreadyExists: false,
                };
                setValidationResponse(ownerFallback);
                setIsEntranceAllowed(true);
                setLobbyTitle("Você é o maestro dessa história!");
                setLobbyMessage("Ajuste suas configurações e clique no botão para entrar.");
                return;
            }

            if (isDevBuild() && isDevTestRoomId(roomId)) {
                const devFallback = applyDevTestLobbyOverride(
                    {
                        isTherapistUser: false,
                        isRoomModerator: false,
                        isUserInMeeting: false,
                        canAccessRoom: false,
                        isTheRoomFull: false,
                        userHasCredits: true,
                        entryApproved: false,
                        sessionRole: "participant",
                        dyteMeetingAlreadyExists: false,
                    },
                    roomId,
                );
                setValidationResponse(devFallback);
                setIsEntranceAllowed(true);
                setLobbyTitle("Sala de teste DEV");
                setLobbyMessage(
                    "Validação da API falhou, mas esta é uma sala de teste em DEV. Libere câmera/microfone e toque em Entrar.",
                );
                return;
            }

            setLobbyTitle("Parece que algo deu errado");
            setLobbyMessage("Ocorreu um problema ao validar seu acesso à sala. Por favor, tente novamente mais tarde.");

            reloadAfterDelay(5000);

            Swal.fire({
                icon: 'error',
                title: 'Problema ao validar acesso',
                text: 'Ocorreu um problema ao validar seu acesso à sala. Por favor, tente novamente mais tarde.',
            });
        }
    }, [roomId, user, validateMeetingEntrance, forceEngine]);

    const saveAvatarSelection = useCallback(async (avatarId: string | null) => {
        if (avatarId && (validationResponse?.sessionRole === 'paciente' || validationResponse?.sessionRole === 'terapeuta')) {
        await updateUserAvatar(avatarId);
      }
    }, [validationResponse, updateUserAvatar]);

    const transitionToRoom = useCallback((currentValidation: SessionSettingsResponse, avatarId: string | null) => {
        if (hasTransitionedToRoomRef.current) {
            return;
        }

        const sessionIdentityKey = ensureSessionIdentityKey();
        hasTransitionedToRoomRef.current = true;
        setIsRequestingEntrance(false);
        onFinishConfiguration(currentValidation, avatarId, sessionIdentityKey);
        void saveAvatarSelection(avatarId);
    }, [onFinishConfiguration, saveAvatarSelection]);

    const requestRoomAccess = useCallback(async () => {
        if (!roomId || !user || isEntranceAllowed) return;

        setIsRequestingEntrance(true);

        try {
            await requestRoomAccessFunction({ roomId, sessionIdentityKey: ensureSessionIdentityKey() });
            setLobbyMessage("Pedido de acesso enviado. Aguarde a liberação pelo moderador da sala.");
        } catch (error) {
            console.error("Erro ao solicitar acesso à sala:", error);
            Swal.fire({
                icon: 'error',
                title: 'Erro ao solicitar acesso',
                text: 'Ocorreu um problema ao solicitar acesso à sala. Por favor, tente novamente mais tarde.',
            });

            setIsRequestingEntrance(false);
        }
    }, [isEntranceAllowed, roomId, user, requestRoomAccessFunction]);

    useEffect(() => {
        let unsubscribe = () => {};
        if (!user) return;

        if (!isEntranceAllowed && !validationResponse?.isRoomModerator) {
            const requestsRef = doc(db, getSessionMeetingCollection(forceEngine), roomId, "accessRequests", user!.uid);
            unsubscribe = onSnapshot(requestsRef, (snapshot) => {
                if (!snapshot.exists() || !validationResponse || validationResponse.isUserInMeeting) {
                    return;
                }
                const data = snapshot.data();
                if (data.status === AccessRoomResponseStatus.APPROVED) {
                    setIsEntranceAllowed(true);
                    setIsRequestingEntrance(false);
                    setLobbyMessage("Acesso aprovado! Entrando na sala...");

                    transitionToRoom({
                        ...validationResponse,
                        entryApproved: true,
                        canAccessRoom: true,
                    }, user?.avatar || null);
                } else if (data.status === AccessRoomResponseStatus.DENIED) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Acesso negado',
                        text: 'Seu pedido de acesso à sala foi negado pelo moderador.',
                    });

                    setIsRequestingEntrance(false);
                    setLobbyTitle("Acesso negado à sala");
                    setLobbyMessage("Seu pedido de acesso à sala foi negado pelo moderador.");
                }
            });
        }

        return () => {
            unsubscribe();
        }
    }, [isEntranceAllowed, validationResponse, roomId, user, transitionToRoom]);

    useEffect(() => {
        if (!isRequestingEntrance || isEntranceAllowed || !validationResponse || validationResponse.isRoomModerator || !user) {
            return;
        }

        let isCancelled = false;
        let timeoutId: number | undefined;
        let attempt = 0;

        const fallbackApprovalCheck = async () => {
            try {
                const { data: response } = await validateMeetingEntrance({
                    roomId,
                    preferLiveKit: forceEngine === "livekit",
                });
                if (isCancelled || !response.entryApproved) {
                    return;
                }

                setValidationResponse(response);
                setIsEntranceAllowed(true);
                setLobbyMessage("Acesso aprovado! Entrando na sala...");
                transitionToRoom(response, user.avatar || null);
            } catch (error) {
                // Falha de rede temporária: manter fallback ativo para próxima tentativa.
            }
        };

        // Polling apenas como rede de segurança (o caminho principal é o
        // onSnapshot de accessRequests). Usamos backoff (3s → 8s) para não
        // sobrecarregar a API do Dyte e evitar rate-limit (429) quando há
        // vários participantes aguardando.
        const scheduleNext = () => {
            const delay = Math.min(3000 + attempt * 1000, 8000);
            attempt += 1;
            timeoutId = window.setTimeout(run, delay);
        };

        const run = async () => {
            if (isCancelled) {
                return;
            }
            await fallbackApprovalCheck();
            if (!isCancelled) {
                scheduleNext();
            }
        };

        void run();

        return () => {
            isCancelled = true;
            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [
        isRequestingEntrance,
        isEntranceAllowed,
        validationResponse,
        user,
        validateMeetingEntrance,
        roomId,
        transitionToRoom,
    ]);

    const handleFinishConfiguration = useCallback((avatarId: string | null) => {
        if (!validationResponse) return;

        if (!validationResponse.isUserInMeeting && (validationResponse.isRoomModerator || isEntranceAllowed)) {
            transitionToRoom(
                isEntranceAllowed
                    ? {
                        ...validationResponse,
                        entryApproved: true,
                        canAccessRoom: true,
                    }
                    : validationResponse,
                avatarId,
            );
            return;
        }

        if (!isRequestingEntrance) {
            requestRoomAccess();
        }
    }, [
        validationResponse,
        isEntranceAllowed,
        isRequestingEntrance,
        transitionToRoom,
        requestRoomAccess
    ]);

    // Auto-reentrada após QUEDA. Se há uma "intenção de reentrada" recente para
    // esta sala (gravada enquanto o usuário estava na sala) e ele já está
    // autorizado (moderador ou aprovado), entramos AUTOMATICAMENTE — sem o
    // usuário precisar tocar em "Entrar"/"Pedir para entrar". Para o caso raro
    // de a aprovação ter expirado, disparamos o pedido de acesso sozinho. Em uma
    // primeira visita (sem intenção salva) nada muda: o lobby normal aparece.
    useEffect(() => {
        if (autoRejoinTriedRef.current || hasTransitionedToRoomRef.current) return;
        if (!user || !validationResponse) return;

        // Respeita todos os bloqueios do lobby.
        if (validationResponse.isUserInMeeting) return;
        if (validationResponse.isTheRoomFull) return;
        if (!validationResponse.userHasCredits && !(isDevBuild() && validationResponse.devTestOpen)) return;

        const intent = readRejoinIntent(roomId, user.uid);
        if (!intent) return;

        const canEnterNow = validationResponse.isRoomModerator || validationResponse.entryApproved || isEntranceAllowed;
        if (canEnterNow) {
            autoRejoinTriedRef.current = true;
            setLobbyMessage("Reconectando você à sala...");
            transitionToRoom(
                {
                    ...validationResponse,
                    entryApproved: true,
                    canAccessRoom: true,
                },
                intent.selectedAvatarId || user.avatar || null,
            );
            return;
        }

        // Estava na sala e caiu, mas a aprovação não está mais ativa: re-knock
        // automático (o onSnapshot/polling existente entra na sala ao aprovar).
        if (validationResponse.canAccessRoom && validationResponse.dyteMeetingAlreadyExists && !isRequestingEntrance) {
            autoRejoinTriedRef.current = true;
            setLobbyMessage("Reconectando você à sala...");
            void requestRoomAccess();
        }
    }, [
        user,
        validationResponse,
        isEntranceAllowed,
        isRequestingEntrance,
        roomId,
        transitionToRoom,
        requestRoomAccess,
    ]);

    useEffect(() => {
        if (user) {
            handleMeetingEntrance();
        }
    }, [handleMeetingEntrance, user]);

    const handleGotoMeeting = () => {
        if (mediaPermissionState !== 'granted') {
            void callSettingsRef.current?.requestMediaAccess();
            return;
        }

        if (forceAvatarSelection) {
            setShowAvatarSelector(true);
            return;
        };

        handleFinishConfiguration(user?.avatar || null);
    }

    // Entrar sem que o preview de câmera/microfone esteja liberado. Útil quando o
    // dispositivo está ocupado/ausente (estado 'unavailable') ou bloqueado: a
    // mídia ainda pode ser ativada dentro da própria sala (RealtimeKit).
    const handleEnterWithoutMedia = () => {
        if (forceAvatarSelection) {
            setShowAvatarSelector(true);
            return;
        }
        handleFinishConfiguration(user?.avatar || null);
    }

    const handleExitLobby = async () => {
        setIsExiting(true);
        try {
            await cancelRoomAccessRequestFunction({ roomId });
        } catch (error) {
            // Ignore errors on canceling access request
        }

        const isGuest = user?.isAnonymous === true;
        if (isGuest) {
            try {
                await logout();
            } catch {
                // segue o redirect mesmo se o signOut falhar
            }
            window.location.href = "/";
            return;
        }

        window.location.href = "/painel";
    }

    const canProceedToMeeting = useMemo(
        () =>
            Boolean(validationResponse) &&
            !isRequestingEntrance &&
            canProceedToDevTestLobby(validationResponse, {
                preferLiveKit: forceEngine === "livekit",
            }),
        [validationResponse, isRequestingEntrance, forceEngine],
    );

    const devLobbyDebugLabel = getDevLobbyDebugLabel();

    const isMediaReady = mediaPermissionState === 'granted';
    const isRequestingMedia = mediaPermissionState === 'requesting';
    const isMediaBlocked = mediaPermissionState === 'denied' || mediaPermissionState === 'unavailable';
    const canEnterWithoutMedia =
        isMediaBlocked &&
        canProceedToMeeting &&
        Boolean(validationResponse?.isRoomModerator || isEntranceAllowed || validationResponse?.devTestOpen);

    const canChangeAvatarBeforeMeeting = useMemo(
        () =>
            isLiveKitOpenTestRoom(roomId) ||
            validationResponse?.livekitOpenTest ||
            validationResponse?.sessionRole === "paciente" ||
            validationResponse?.sessionRole === "terapeuta",
        [validationResponse, roomId],
    );

    const getEntranceButtonLabel = () => {
        if (isRequestingMedia) {
            return 'Solicitando permissão...';
        }

        if (!isMediaReady) {
            return 'Liberar câmera e microfone';
        }

        if (isRequestingEntrance) {
            return 'Aguardando liberação...';
        }

        if (!canProceedToMeeting) {
            return 'Aguarde...';
        }

        if (forceAvatarSelection) {
            return 'Selecionar Avatar';
        }

        if (validationResponse?.isRoomModerator || isEntranceAllowed) {
            return 'Entrar';
        }

        return 'Solicitar entrada';
    }

    const getPrimaryActionHint = () => {
        if (isRequestingMedia) {
            return 'Quando o navegador perguntar, toque em Permitir. Se nada aparecer em alguns segundos, toque de novo.';
        }

        if (isMediaBlocked) {
            return canEnterWithoutMedia
                ? 'Não conseguimos acessar câmera/microfone (pode estar em uso por outra aba/app). Toque em "Tentar liberar novamente" ou use "Entrar mesmo assim".'
                : 'Não conseguimos acessar câmera/microfone. Feche outras abas/apps que usam a câmera e toque em "Tentar liberar novamente".';
        }

        if (!isMediaReady) {
            return 'Passo 1: toque no botão verde para liberar câmera e microfone.';
        }

        if (forceAvatarSelection) {
            return 'Passo 2: escolha um avatar para concluir sua entrada na sala.';
        }

        if (validationResponse?.devTestOpen) {
            return 'Sala de teste DEV: libere câmera/microfone e toque em Entrar.';
        }

        if (validationResponse?.isRoomModerator || isEntranceAllowed) {
            return 'Tudo pronto. Toque no botão para entrar na sala agora.';
        }

        return 'Toque no botão para solicitar sua entrada e aguarde a aprovação do moderador.';
    };

    return (
        <div className="pt-4 pb-28 lg:pb-32 pl-4 pr-4 flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-[#F4FBFA] to-white">
            <div className="bg-white rounded-3xl shadow-2xl mx-auto overflow-hidden w-full lg:w-1/2 pt-8 relative">
                <button
                    disabled={!validationResponse || isExiting}
                    type="button"
                    onClick={handleExitLobby}
                    className="absolute top-4 right-4 flex items-center gap-1 rounded-full bg-red-500 px-3 py-1 text-sm font-semibold text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:bg-red-300"
                    aria-label="Voltar ao painel"
                >
                    {isExiting ? (
                        <>
                            Saindo...
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        </>
                    ) : (
                        <>
                            Sair
                            <X className="h-4 w-4" />
                        </>
                    )}
                </button>
                <div className="flex items-center justify-center mb-4">
                    <img
                        src="/assets/logo.png"
                        alt="Constela Brasil"
                        className="h-32 w-auto"
                    />
                </div>
                <div className="mb-6 text-center px-6">
                    <h3 className="text-2xl font-bold text-gray-800 mb-4">
                        {lobbyTitle}
                    </h3>
                    <div className="text-gray-600">
                        {lobbyMessage}
                    </div>
                    {networkWarning && (
                        <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-left text-sm text-amber-800">
                            <WifiOff className="mt-0.5 h-4 w-4 flex-shrink-0" />
                            <span>{networkWarning}</span>
                        </div>
                    )}
                    {devLobbyDebugLabel && (
                        <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                            {devLobbyDebugLabel}
                            {validationResponse?.devTestOpen
                                ? " · sala de teste: entrada liberada"
                                : ""}
                        </p>
                    )}
                </div>
                <div className="flex flex-col justify-center">
                    <CallSettings ref={callSettingsRef} onPermissionStateChange={setMediaPermissionState} />
                    {showAvatarSelector && (
                        <AvatarSelector
                            onConfirm={async (avatarId) => {
                                await saveAvatarSelection(avatarId);
                                setShowAvatarSelector(false);
                                if (forceAvatarSelection) {
                                    setForceAvatarSelection(false);
                                }
                            }}
                            onClose={() => setShowAvatarSelector(false)}
                            isModal={true}
                            forceAvatarSelection={forceAvatarSelection || isLiveKitOpenTestRoom(roomId)}
                        />
                    )}
                </div>
            </div>

            <div className="fixed inset-x-0 bottom-0 z-40 hidden border-t border-gray-200 bg-white/95 px-6 py-4 backdrop-blur lg:block">
                <div className="mx-auto max-w-3xl">
                    <p className="mb-3 text-center text-sm text-gray-600">{getPrimaryActionHint()}</p>
                    <div className="flex justify-center gap-3">
                        {canChangeAvatarBeforeMeeting && (
                            <button
                                type="button"
                                disabled={!canProceedToMeeting || !isMediaReady}
                                className="rounded-2xl bg-gray-200 px-5 py-3 text-sm font-semibold text-gray-700 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                                onClick={() => setShowAvatarSelector(true)}
                            >
                                Avatar
                            </button>
                        )}
                        <button
                            type="button"
                            disabled={(!canProceedToMeeting && isMediaReady) || isRequestingMedia}
                            className="min-w-[220px] rounded-2xl bg-teal-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                            onClick={handleGotoMeeting}
                        >
                            {canProceedToMeeting || !isMediaReady || isRequestingMedia ? getEntranceButtonLabel() : 'Aguarde...'}
                        </button>
                        {canEnterWithoutMedia && (
                            <button
                                type="button"
                                onClick={handleEnterWithoutMedia}
                                className="rounded-2xl border border-teal-600 px-5 py-3 text-sm font-semibold text-teal-700 transition-colors hover:bg-teal-50"
                            >
                                Entrar mesmo assim
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 px-4 pb-[max(env(safe-area-inset-bottom),1rem)] pt-3 backdrop-blur lg:hidden">
                <div className="mx-auto max-w-lg">
                    <p className="mb-3 text-center text-sm text-gray-600">{getPrimaryActionHint()}</p>
                    <div className="flex gap-3">
                        {canChangeAvatarBeforeMeeting && (
                            <button
                                type="button"
                                disabled={!canProceedToMeeting || !isMediaReady}
                                className="flex-1 rounded-2xl bg-gray-200 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                                onClick={() => setShowAvatarSelector(true)}
                            >
                                Avatar
                            </button>
                        )}
                        <button
                            type="button"
                            disabled={(!canProceedToMeeting && isMediaReady) || isRequestingMedia}
                            className="flex-[2] rounded-2xl bg-teal-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-colors disabled:cursor-not-allowed disabled:bg-gray-400"
                            onClick={handleGotoMeeting}
                        >
                            {canProceedToMeeting || !isMediaReady || isRequestingMedia ? getEntranceButtonLabel() : 'Aguarde...'}
                        </button>
                    </div>
                    {canEnterWithoutMedia && (
                        <button
                            type="button"
                            onClick={handleEnterWithoutMedia}
                            className="mt-3 w-full rounded-2xl border border-teal-600 px-4 py-3 text-sm font-semibold text-teal-700 transition-colors hover:bg-teal-50"
                        >
                            Entrar mesmo assim
                        </button>
                    )}
                </div>
            </div>

            {/* Modal de Login Anônimo */}
            {showAnonymousLoginModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
                        <div className="flex items-center justify-center mb-6">
                            <img
                                src="/assets/logo.png"
                                alt="Constela Brasil"
                                className="h-20 w-auto"
                            />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">
                            Bem-vindo!
                        </h2>
                        <p className="text-gray-600 mb-6 text-center">
                            Para acessar a sala, por favor informe seu nome:
                        </p>
                        <input
                            type="text"
                            value={anonymousUserName}
                            onChange={(e) => setAnonymousUserName(e.target.value)}
                            placeholder="Digite seu nome"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 mb-4"
                            disabled={isAuthenticating}
                            autoFocus
                        />
                        <div className="mb-6 text-xs text-gray-500 text-center">
                            O acesso e temporario e sera encerrado quando voce fechar o navegador.
                        </div>
                        <button
                            onClick={handleAnonymousLogin}
                            disabled={isAuthenticating || !anonymousUserName.trim()}
                            className="w-full bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold text-lg hover:bg-teal-700 transition-colors shadow-md disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                            {isAuthenticating ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                    Entrando...
                                </>
                            ) : (
                                'Continuar'
                            )}
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
};

export default Lobby;